import React, { useEffect, useState } from 'react';
import { View, Text } from 'react-native';
import { useLinkTo } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import LossSummary from './LossSummary';
import LossDetails from './LossDetails';
import { resetLossCounts, updateLossPayload } from '../../helpers/LossHelpers';
import { useLossModals } from './useLossModals';
import {
  useGetLossByCompanyIdQuery,
  useCreateLossMutation,
  useUpdateLossMutation,
  useDeleteLossMutation,
} from 'src/features/application/api/lossApi';
import { selectLosses, setSelectedLoss, setLosses } from 'src/features/application/slice/lossSlice';
import { RootState } from 'src/store/store';
import { Loss } from '@typeRoots/common/loss';
import { Selection, ViewState } from './LossTypes';
import { styles } from './LossScreen.styles';
import { initialLossState } from 'src/features/application/slice/initialState';
import { URLS } from 'src/constants/urls';
import { toggleToastState } from 'src/helpers/toastHelper';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';

const LossScreen: React.FC = () => {
  const dispatch = useDispatch();
  const linkTo = useLinkTo();

  //error toast
  const [lossSaveErrorToast, setLossSaveErrorToast] = useState(false);
  const [lossDeleteErrorToast, setLossDeleteErrorToast] = useState(false);
  const [lossDeleteSuccessToast, setLossDeleteSuccessToast] = useState(false);
  // Selectors and API hooks
  const currentCompany = useSelector((state: RootState) => state.company.company);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  const companyId = currentCompany?.id || 0;
  const losses = useSelector((state: RootState) => selectLosses(state));
  const { data: lossesData = [], isLoading, error } = useGetLossByCompanyIdQuery(companyId);
  const [createLoss] = useCreateLossMutation();
  const [updateLoss] = useUpdateLossMutation();
  const [deleteLoss] = useDeleteLossMutation();

  // Local States
  const [view, setView] = useState<ViewState>('summary');
  const [newLoss, setNewLoss] = useState<Loss>(initialLossState); // Updated to match backend format
  const [toastVisible, setToastVisible] = useState(false);
  const [selection, setSelection] = useState<Selection[]>([]);
  const [userHasLosses, setUserHasLosses] = useState(false); // User-controlled Yes/No state

  const {
    isYearModalVisible,
    setYearModalVisible,
    isChecklistModalVisible,
    setChecklistModalVisible,
    handleModalYearSelect,
  } = useLossModals();

  // Dynamically calculate hasLosses
  const hasLosses = losses.length > 0;

  useEffect(() => {
    trackGTMEvent(
      'initial-losses-page-landed',
      {
        userEvent: 'User lands on Losses page after Policy Questions page.',
      },
      accountType,
    );
  }, []);

  useEffect(() => {
    // Track landing events when the view changes to Loss Details or Loss Summary
    if (view === 'details') {
      trackGTMEvent(
        'loss-details-screen-rendered',
        {
          userEvent: 'User pressed on Add Loss and rendered Add Loss',
        },
        accountType,
      );
    }

    if (view === 'summary') {
      trackGTMEvent(
        'loss-summary-screen-rendered',
        {
          userEvent: 'User rendered the Loss Summary screen',
        },
        accountType,
      );
    }

    // Track leaving events when the user navigates away from Loss Details or Loss Summary
    return () => {
      if (view === 'details') {
        trackGTMEvent(
          'loss-details-screen-left',
          {
            userEvent: 'User pressed on Back and leaves the Add Loss screen',
          },
          accountType,
        );
      }

      if (view === 'summary') {
        trackGTMEvent(
          'loss-summary-screen-left',
          {
            userEvent:
              'User press on Back and leaves the Loss Summary screen, navigating to the Policy Questions page.',
          },
          accountType,
        );
      }
    };
  }, [view]); // Track changes to the `view` state (Loss Details or Loss Summary)

  // Sync userHasLosses with hasLosses
  useEffect(() => {
    setUserHasLosses(hasLosses); // Update userHasLosses when Redux-based hasLosses changes
  }, [hasLosses]);

  // Sync Redux state with API data
  useEffect(() => {
    if (lossesData.length > 0) {
      dispatch(setLosses(lossesData));
    }
  }, [lossesData, dispatch]);

  // Handlers
  const handleAddLoss = () => {
    trackGTMEvent(
      'add-loss-clicked',
      {
        userEvent: 'User clicked on Add Loss button to add a new loss.',
      },
      accountType,
    );

    setNewLoss(initialLossState);
    setSelection([]);
    setView('details');
  };

  const handleEditLoss = (index: number) => {
    // Track "Loss Edited" event
    trackGTMEvent(
      'loss-edited',
      {
        userEvent: 'User pressed on edit button to edit the loss',
      },
      accountType,
    );

    const selected = losses[index];
    dispatch(setSelectedLoss(selected));
    setNewLoss(selected);

    setView('details');
  };

  const handleDeleteLoss = async (index: number) => {
    // Track "Loss Deleted" event
    trackGTMEvent(
      'loss-deleted',
      {
        userEvent: 'User press on delete button and loss is deleted.',
      },
      accountType,
    );

    const lossToDelete = losses[index];
    if (lossToDelete.id !== undefined) {
      try {
        await deleteLoss(lossToDelete.id).unwrap();
        const updatedLosses = losses.filter((_, i) => i !== index);
        dispatch(setLosses(updatedLosses));
        setLossDeleteSuccessToast(true);
        toggleToastState(setLossDeleteSuccessToast);
      } catch (err) {
        console.error('Failed to delete loss:', err);
      }
    } else {
      setLossDeleteErrorToast(true);
      toggleToastState(setLossDeleteErrorToast);
      console.error('Loss ID is undefined');
    }
  };

  const handleSaveLoss = async () => {
    try {
      // Only reset loss counts if it's a new loss
      const updatedLoss = newLoss.id ? newLoss : resetLossCounts(newLoss, selection); // Don't reset counts for existing losses
      const lossPayload = updateLossPayload(updatedLoss, companyId);

      // Track "Loss Details Given" event before saving the loss details
      trackGTMEvent(
        'loss-details-given',
        {
          userEvent: 'User fills out the loss details and press Next button',
        },
        accountType,
      );

      if (newLoss.id) {
        const updatedLossFromAPI = await updateLoss({ ...lossPayload, id: newLoss.id }).unwrap();
        dispatch(
          setLosses(
            losses.map((loss) => (loss.id === updatedLossFromAPI.id ? updatedLossFromAPI : loss)),
          ),
        );
      } else {
        const createdLoss = await createLoss(lossPayload).unwrap();
        dispatch(setLosses([...losses, createdLoss]));
      }

      setView('summary');
      setToastVisible(true);
      setTimeout(() => setToastVisible(false), 3000);
    } catch (err) {
      setLossSaveErrorToast(true);
      toggleToastState(setLossSaveErrorToast);
      console.error('Failed to save loss:', err);
    }
  };

  const handleSelectYear = (year: number) => {
    handleModalYearSelect(year, (updatedLoss) => setNewLoss({ ...newLoss, ...updatedLoss }));
  };

  const handleSelectionChange = (selectedClaims: Selection[]) => {
    setSelection(selectedClaims);

    const updatedLoss = resetLossCounts(newLoss, selectedClaims);
    setNewLoss({ ...newLoss, ...updatedLoss });
  };

  const handleNext = () => {
    // Track "Loss Summary Confirmed" event
    trackGTMEvent(
      'loss-summary-confirmed',
      {
        userEvent:
          'User press Next to confirm loss details and navigates to Acknowledgement screen',
      },
      accountType,
    );

    linkTo(URLS.APPLICATION.CONFIRM);
  };

  const handleBack = () => {
    trackGTMEvent(
      'initial-losses-page-left',
      {
        userEvent: 'User clicked Back and left the Losses page.',
      },
      accountType,
    );

    linkTo(
      accountType === 'producer' ? URLS.APPLICATION.GROWTH_AND_OWNERSHIP : URLS.APPLICATION.POLICY,
    );
  };

  if (isLoading) {
    return (
      <View>
        <Text>Loading losses...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View>
        <Text>Error fetching losses. Please try again later.</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {view === 'summary' && (
        <LossSummary
          losses={losses}
          hasLosses={hasLosses}
          userHasLosses={userHasLosses}
          setUserHasLosses={setUserHasLosses}
          onAddLoss={handleAddLoss}
          onEditLoss={handleEditLoss}
          onDeleteLoss={handleDeleteLoss}
          onNext={handleNext}
          onBack={handleBack}
          toastVisible={toastVisible}
          lossDeleteErrorToast={lossDeleteErrorToast}
          lossDeleteSuccessToast={lossDeleteSuccessToast}
        />
      )}
      {view === 'details' && (
        <LossDetails
          loss={newLoss}
          lossSaveErrorToast={lossSaveErrorToast}
          onSaveLoss={handleSaveLoss}
          onUpdateLoss={(updatedLoss: React.SetStateAction<Loss>) =>
            setNewLoss({ ...newLoss, ...updatedLoss })
          }
          onUpdateSelection={setSelection}
          onBack={() => setView('summary')}
          // Pass modal-related props to LossDetails
          isYearModalVisible={isYearModalVisible}
          setYearModalVisible={setYearModalVisible}
          isChecklistModalVisible={isChecklistModalVisible}
          setChecklistModalVisible={setChecklistModalVisible}
          handleSelectYear={handleSelectYear}
          handleSelectionChange={handleSelectionChange}
        />
      )}
    </View>
  );
};

export default LossScreen;
